import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Modal, Form } from 'react-bootstrap';
import { Plus, Pencil, Trash2, GripVertical } from 'react-bootstrap-icons';
import { useAdminMenu } from '../../context/AdminMenuContext';

const CategoryModal = ({ show, onHide, category, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    isActive: true,
  });

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name,
        slug: category.slug,
        description: category.description || '',
        isActive: category.isActive,
      });
    } else {
      setFormData({
        name: '',
        slug: '',
        description: '',
        isActive: true,
      });
    }
  }, [category, show]);

  const handleNameChange = (name) => {
    const slug = name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/đ/g, 'd')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

    setFormData({ ...formData, name, slug });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{category ? 'Sửa danh mục' : 'Thêm danh mục mới'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Tên danh mục *</Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              required
              placeholder="Nhập tên danh mục"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Slug *</Form.Label>
            <Form.Control
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              required
              placeholder="slug-danh-muc"
            />
            <Form.Text className="text-muted">Tự động tạo từ tên danh mục</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Mô tả</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Nhập mô tả danh mục"
            />
          </Form.Group>

          <Form.Group>
            <Form.Check
              type="switch"
              id="category-active"
              label="Hoạt động"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={onHide}
          >
            Hủy
          </Button>
          <Button
            variant="primary"
            type="submit"
          >
            {category ? 'Cập nhật' : 'Thêm'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const AdminCategories = () => {
  const { categories, menuItems, createCategory, updateCategory, deleteCategory, reorderCategories } = useAdminMenu();

  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  const sortedCategories = [...categories].sort((a, b) => a.displayOrder - b.displayOrder);

  const handleEdit = (category) => {
    setEditingCategory(category);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingCategory(null);
    setShowModal(true);
  };

  const handleSave = async (formData) => {
    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, formData);
      } else {
        const newCategory = {
          ...formData,
          displayOrder: categories.length + 1,
          image: '/images/placeholder.jpg',
        };
        await createCategory(newCategory);
      }
      setShowModal(false);
      setEditingCategory(null);
    } catch (error) {
      console.error('Error saving category:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bạn có chắc muốn xóa danh mục này? Tất cả món trong danh mục cũng sẽ bị xóa.')) {
      try {
        await deleteCategory(id);
      } catch (error) {
        console.error('Error deleting category:', error);
      }
    }
  };

  return (
    <>
      <Card className="shadow-sm">
        <Card.Header className="bg-white border-bottom">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">Danh mục sản phẩm</h4>
              <small className="text-muted">Quản lý các danh mục menu</small>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={handleAdd}
            >
              <Plus
                size={18}
                className="me-1"
              />
              Thêm
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="d-flex flex-column gap-3">
            {sortedCategories.map((category) => {
              const itemCount = menuItems.filter((item) => item.categoryId === category.id).length;
              const availableCount = menuItems.filter(
                (item) => item.categoryId === category.id && item.isAvailable,
              ).length;

              return (
                <div
                  key={category.id}
                  className="d-flex align-items-center gap-3 p-3 border rounded bg-light"
                >
                  <div className="cursor-grab">
                    <GripVertical
                      size={20}
                      className="text-muted"
                    />
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <h5 className="mb-0">{category.name}</h5>
                      {category.isActive && (
                        <Badge
                          bg="success"
                          pill
                        >
                          Hoạt động
                        </Badge>
                      )}
                      <span className="text-muted small">#{category.displayOrder}</span>
                    </div>
                    <p className="text-muted mb-1 small">{category.description}</p>
                    <small className="text-muted">
                      {availableCount}/{itemCount} món
                    </small>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleEdit(category)}
                    >
                      <Pencil size={16} />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(category.id)}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {sortedCategories.length === 0 && (
            <div className="text-center py-5 text-muted">Chưa có danh mục nào. Hãy thêm danh mục đầu tiên!</div>
          )}
        </Card.Body>
      </Card>

      <CategoryModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setEditingCategory(null);
        }}
        category={editingCategory}
        onSave={handleSave}
      />
    </>
  );
};

export default AdminCategories;
